import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import AttendLogo from '../assets/attendlogo.png';
import { validatePassword, passwordErrorMessage } from '../utils/passwordValidation';
import AuthShell, { authAlertSx, authButtonSx, authTextFieldSx } from '../components/AuthShell';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  useEffect(() => {
    if (!token || !emailFromUrl) {
      setError(t('resetPassword.invalidLink'));
    }
  }, [token, emailFromUrl, t]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError) {
      const validation = validatePassword(value);
      setPasswordError(validation.valid ? '' : (validation.error || ''));
    }
    if (confirmError && value === passwordConfirmation) {
      setConfirmError('');
    }
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasswordConfirmation(value);
    if (confirmError) {
      setConfirmError(value === password ? '' : t('resetPassword.passwordsDoNotMatch'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const passValidation = validatePassword(password);
    if (!passValidation.valid) {
      setPasswordError(passValidation.error || '');
      return;
    }

    if (password !== passwordConfirmation) {
      setConfirmError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    if (!token || !emailFromUrl) {
      setError(t('resetPassword.missingTokenEmail'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        email: emailFromUrl,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setSuccess(response.message || t('resetPassword.resetSuccess'));
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError(t('resetPassword.tooManyRequests'));
      } else {
        setError(err.response?.data?.message || t('resetPassword.resetFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            px: 1.5,
            py: 1,
            borderRadius: 'var(--an-radius-md)',
            border: '1px solid var(--an-border-light)',
            backgroundColor: 'var(--an-surface)',
            boxShadow: 'var(--an-shadow-sm)',
          }}
        >
          <Box component="img" src={AttendLogo} alt="AttendNow" sx={{ height: 44, width: 'auto', display: 'block' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--an-radius-sm)',
              color: 'var(--an-primary)',
              backgroundColor: 'var(--an-primary-light)',
              flexShrink: 0,
            }}
          >
            <LockIcon />
          </Box>
          <Typography variant="h4" component="h1" fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
            {t('resetPassword.title')}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'var(--an-text-secondary)', maxWidth: 440 }}>
          {t('resetPassword.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={authAlertSx}>
          {error}
          {!token || !emailFromUrl ? (
            <Box sx={{ mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" sx={{ fontWeight: 800, color: 'inherit' }}>
                {t('resetPassword.requestNewLink')}
              </Link>
            </Box>
          ) : null}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={authAlertSx}>
          {success} {t('resetPassword.redirectingToLogin')}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('resetPassword.emailLabel')}
          value={emailFromUrl || ''}
          disabled
          margin="normal"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('resetPassword.newPasswordLabel')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          required
          margin="normal"
          error={!!passwordError}
          helperText={passwordError || passwordErrorMessage}
          disabled={loading || !!success || !token}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'var(--an-text-secondary)' }}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('resetPassword.confirmPasswordLabel')}
          type={showPassword ? 'text' : 'password'}
          value={passwordConfirmation}
          onChange={handleConfirmChange}
          required
          margin="normal"
          error={!!confirmError}
          helperText={confirmError}
          disabled={loading || !!success || !token}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !!success || !token || !!passwordError || !!confirmError}
          sx={{ ...authButtonSx, mb: 3 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('resetPassword.resetButton')}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              fontWeight: 800,
              color: 'var(--an-primary)',
              '&:hover': {
                color: 'var(--an-primary-dark)',
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, mr: 1 }} />
            {t('resetPassword.backToSignIn')}
          </Link>
        </Box>
      </Box>
    </AuthShell>
  );
};

export default ResetPassword;
